import { capitalise, getAnonymousUserId } from '@get-mosh/msh-shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { trackStartQuiz } from 'src/analytics/Events';
import ApiRoutes from 'src/constants/ApiRoutes';
import PortalRoutes from 'src/constants/PortalRoutes';
import Routes from 'src/constants/Routes';
import { Vertical } from 'src/constants/Verticals';
import { env } from 'src/env.mjs';
import { getQuizBaseUrl } from 'src/helpers/quiz';
import { camelizeObjectKeys } from 'src/helpers/utils';
import { getHeaders } from 'src/services/api';
import { ApiError } from 'src/types/Api';
import { QUIZ_FLOW_TYPES, QuizFlow, QuizRecord } from 'src/types/Quiz';

type StartQuizParams = {
    quizType: Vertical;
    quizVersion?: string;
    quizToken?: string;
    quizFlow: QuizFlow;
    referralCode?: string;
    subscriptionId?: string;
    quizSubVersion?: string;
    quizResumeSource?: string;
};

async function startQuiz({
    quizType,
    quizVersion = '',
    quizToken,
    quizFlow,
    referralCode = '',
    subscriptionId = '',
    quizSubVersion = '',
    quizResumeSource = '',
}: StartQuizParams) {
    const searchParams = new URLSearchParams(window.location.search);
    // Body
    const formData = new FormData();
    formData.append(
        'quiz_type',
        quizType.charAt(0).toUpperCase() + quizType.slice(1).toLowerCase(),
    );
    formData.append('quiz_version', quizVersion ? quizVersion : '');
    formData.append('quiz_brand', capitalise(env.NEXT_PUBLIC_BRAND));
    formData.append('quiz_token', quizToken ? quizToken : '');
    formData.append('quiz_flow', quizFlow);
    formData.append('subscription_id', subscriptionId ? subscriptionId : '');
    formData.append('referral_code', referralCode ? referralCode : '');
    if (quizResumeSource) {
        formData.append('quiz_resume_source', quizResumeSource);
    }
    if (quizSubVersion) {
        formData.append('quiz_sub_version', quizSubVersion);
    }
    if (
        quizFlow === QUIZ_FLOW_TYPES.initial ||
        quizFlow === QUIZ_FLOW_TYPES.symptoms
    ) {
        const trackingData = {
            anonymous_id: getAnonymousUserId() || '',
            utm_source: searchParams?.get('utm_source') || '',
            utm_medium: searchParams?.get('utm_medium') || '',
            source_stack: searchParams?.get('source_stack') || '',
            last_channel_source: searchParams?.get('last_channel_source') || '',
        };
        formData.append('tracking_data', JSON.stringify(trackingData));
    }
    // Request
    const routeByType = {
        [QUIZ_FLOW_TYPES.initial]: ApiRoutes.INITIAL_QUIZZES_START_QUIZ,
        [QUIZ_FLOW_TYPES.renewal]: ApiRoutes.RENEWAL_QUIZZES_START_QUIZ,
        [QUIZ_FLOW_TYPES.symptoms]:
            ApiRoutes.HEALTH_TRACKING_QUIZZES_START_QUIZ,
    };
    const route = routeByType[quizFlow];
    const headers = getHeaders();
    // Remove content type header to allow browser to set it automatically
    headers.delete('Content-Type');

    const res = await fetch(route, {
        method: 'POST',
        headers,
        body: formData,
    });

    const data = await res.json();

    if (!res.ok) {
        const error = data as ApiError;
        window.location.href = Routes.ERROR({
            error_type: error.type,
            error_message: error.message,
            cta_text: 'Go to account',
            cta_url: PortalRoutes.NOTIFICATIONS,
            error_title: error.title || 'There was an error',
        });
        throw error;
    }

    const quizRecord = camelizeObjectKeys(data) as QuizRecord;

    if (!quizToken) {
        trackStartQuiz(Vertical[quizType.toUpperCase()], quizFlow, {
            quiz_name: `${quizRecord.quizName} - ${quizVersion} ${quizSubVersion}`,
            label: 'Quiz Started',
        });

        const quizBaseUrl = getQuizBaseUrl({
            quizFlow,
            quizType,
            quizToken: quizRecord.quizToken ?? '',
            quizVersion: quizRecord.quizVersion,
            quizSubVersion: quizRecord.quizSubVersion,
            quizResumeSource,
            subscriptionId,
        });

        window.history.replaceState(null, '', quizBaseUrl);
    }

    return quizRecord;
}

export const useStartQuiz = ({
    quizType,
    quizVersion = '',
    quizToken,
    quizFlow,
    referralCode = '',
    subscriptionId = '',
    quizSubVersion = '',
    quizResumeSource = '',
}: StartQuizParams) => {
    const queryClient = useQueryClient();

    return useQuery({
        queryKey: ['Quiz', quizToken],
        queryFn: async () => {
            const response = await startQuiz({
                quizType,
                quizVersion,
                quizToken,
                quizFlow,
                referralCode,
                subscriptionId,
                quizSubVersion,
                quizResumeSource,
            });

            // Prevent the query from running again
            queryClient.setQueryData(['Quiz', response.quizToken], response);

            return response;
        },
    });
};
